import { useMemo } from "react";
import dayjs from "dayjs";
import { useSelection } from "../contexts/SelectionContext";
import { useDate } from "../contexts/DateContext";
import { useTimezone } from "../contexts/TimezoneContext";
import { useCurrentSimulationResults } from "../api/simulations";
import { useEnsembleMember } from "../contexts/EnsembleMemberContext";

const IGNITION_SELECTION_PATTERN = /^ignition-(\d+)$/;

/** Find the result entry for the currently selected date. */
const findDayResult = (
  selectedDate: dayjs.Dayjs | null,
  selectedTimezone: string,
  results: any[],
): any | null => {
  if (!selectedDate || !results?.length) return null;
  const selectedDateOnly = selectedDate.format("YYYY-MM-DD");
  return (
    results.find(
      (data) =>
        dayjs(data.start_date_time).tz(selectedTimezone).format("YYYY-MM-DD") ===
        selectedDateOnly,
    ) ?? null
  );
};

/** Array index encoded in a selection id like "ignition-3", else null. */
const selectedIgnitionIndex = (selectedPolygonId: string | null): number | null => {
  if (typeof selectedPolygonId !== "string") return null;
  const match = selectedPolygonId.match(IGNITION_SELECTION_PATTERN);
  return match ? parseInt(match[1], 10) : null;
};

/** Global partition index from an ignition's partition key (e.g. "42_0" → 42). */
const partitionIndex = (ignition: any): number => {
  const idx = parseInt(String(ignition?.partition ?? "").split("_")[0], 10);
  return Number.isNaN(idx) ? -1 : idx;
};

const toNumericIds = (ids: unknown[]): number[] =>
  ids.map((id) => Number(id)).filter((id): id is number => Number.isFinite(id));

/**
 * Ensemble: impacted IDs for the slider-selected member. Ensembles fan the
 * base ignition points out as contiguous blocks of pointsPerMember partitions
 * (member m owns partition idxs [m*ppm, (m+1)*ppm)), and every instance
 * carries its own precomputed impacted_asset_ids. With an ignition selected we
 * remap its base point to the member's instance of it (the selection id always
 * references the FIRST instance at that location — see RiskFromLayer — so it
 * must be remapped even for member 0's own block ordering); with none selected
 * we union the whole member block, matching the whole-member animation view.
 */
const memberImpactedIds = (
  ignitions: any[],
  selectedIdx: number | null,
  memberIdx: number,
  pointsPerMember: number,
): unknown[] => {
  if (selectedIdx !== null) {
    const base = ignitions[selectedIdx];
    if (!base) return [];
    const baseGlobalIdx = partitionIndex(base);
    const pointIdx =
      (baseGlobalIdx >= 0 ? baseGlobalIdx : selectedIdx) % pointsPerMember;
    const targetIdx = memberIdx * pointsPerMember + pointIdx;
    const target = ignitions.find((ig) => partitionIndex(ig) === targetIdx);
    return target?.impacted_asset_ids ?? [];
  }

  const lo = memberIdx * pointsPerMember;
  const hi = lo + pointsPerMember;
  const union = new Set<unknown>();
  for (const ig of ignitions) {
    const idx = partitionIndex(ig);
    if (idx < lo || idx >= hi) continue;
    for (const id of ig?.impacted_asset_ids ?? []) union.add(id);
  }
  return Array.from(union);
};

/**
 * Returns the set of asset IDs impacted by the currently-selected simulation
 * ignition. Empty when nothing relevant is selected.
 *
 * The impacted IDs are precomputed server-side (PostGIS intersection of each
 * ignition's final animation frame against the project's assets) and arrive on
 * the ignition as `impacted_asset_ids`, so this no longer fetches the full
 * asset GeoJSON — the assets are rendered from vector tiles. This hook only
 * maps the current selection to the right ignition's precomputed IDs.
 *
 * For ensembles the AnimationLayer publishes the scenario slider's committed
 * member via EnsembleMemberContext, and the IDs come from that member's
 * instance of the selected point — or the union over the member's whole block
 * when no point is selected — so the red assets track the member being played.
 */
export const useImpactedAssetIds = (): number[] => {
  const { selectedPolygonId } = useSelection();
  const { selectedDate } = useDate();
  const { selectedTimezone } = useTimezone();
  const { data: simResults } = useCurrentSimulationResults();
  const { memberSelection } = useEnsembleMember();

  return useMemo(() => {
    const results = simResults?.data?.results ?? [];
    const dayResult = findDayResult(selectedDate, selectedTimezone, results);
    const ignitions: any[] = dayResult?.ignitions ?? [];
    if (!ignitions.length) return [];

    const selectedIdx = selectedIgnitionIndex(selectedPolygonId);

    if (memberSelection) {
      return toNumericIds(
        memberImpactedIds(
          ignitions,
          selectedIdx,
          memberSelection.memberIdx,
          memberSelection.pointsPerMember,
        ),
      );
    }

    if (selectedIdx === null) return [];
    return toNumericIds(ignitions[selectedIdx]?.impacted_asset_ids ?? []);
  }, [
    selectedPolygonId,
    selectedDate,
    selectedTimezone,
    simResults,
    memberSelection,
  ]);
};
